<template>
  <div>
    <div class="ibox" data-cy="funnel-review">
      <div class="ibox-content p-t-md">
        <h1 class="text-20 font-heavy m-y-none">{{ heading }}</h1>
        <p class="text-gray-600 m-b-md m-t-xs">
          We've prefilled what we have on file. Tap Edit on any section that
          needs an update, otherwise confirm and continue to payment.
        </p>

        <!-- Parent / Guardian card -->
        <section
          :class="cardClass()"
          :style="cardStyle()"
          data-cy="review-parent-card"
        >
          <div :class="cardHeaderClass()">
            <h3 class="m-y-none font-heavy flex-1 min-w-0">
              {{ parentSummary.name }}
            </h3>
            <button
              class="btn btn-default btn-sm flex-shrink-0"
              :class="{ disabled: !editEnabled }"
              :disabled="!editEnabled"
              data-cy="review-edit-parent"
              @click="editStep('personal_info')"
            >
              <i class="fa-solid fa-pencil m-r-xxs"></i>Edit Info
            </button>
          </div>
          <div :class="cardBodyClass()">
            <div class="flex flex-col gap-xs">
              <div
                v-if="parentSummary.email"
                class="flex align-items-center gap-sm"
              >
                <i class="fa-regular fa-envelope text-gray-400"></i>
                <span class="text-gray-700">{{ parentSummary.email }}</span>
              </div>
              <div
                v-if="parentSummary.phone"
                class="flex align-items-center gap-sm"
              >
                <i class="fa-solid fa-phone text-gray-400"></i>
                <span class="text-gray-700">{{ parentSummary.phone }}</span>
              </div>
            </div>

            <!-- Registration Q&A for parent -->
            <div
              v-if="parentQuestions.length > 0"
              class="border-t border-solid border-green-300 m-t-sm p-t-sm"
            >
              <div
                v-for="(group, groupIdx) in groupQuestions(parentQuestions)"
                :key="`pg-${groupIdx}`"
                :class="groupIdx > 0 ? 'm-t-sm' : ''"
              >
                <h4 v-if="group.name" class="font-heavy m-b-xs m-t-none">
                  {{ group.name }}
                </h4>
                <div class="grid grid-cols-2 md-grid-cols-1 gap-4">
                  <div v-for="q in group.questions" :key="`pq-${q.id}`">
                    <p class="text-12 text-gray-500 m-b-none">{{ q.prompt }}</p>
                    <template v-if="q.type === 'RegPhotoQuestion'">
                      <a
                        v-if="q.value"
                        :href="q.value"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          :src="q.value"
                          :alt="q.prompt"
                          class="w-24 h-24 object-cover rounded-xxs border border-solid border-gray-200"
                        />
                      </a>
                      <p v-else class="text-gray-700 m-b-none">—</p>
                    </template>
                    <p
                      v-else
                      class="text-gray-700 m-b-none"
                      :class="{
                        'whitespace-pre-line': q.type === 'RegAddressQuestion',
                      }"
                    >
                      {{ q.value || "—" }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Wrestler cards -->
        <section
          v-for="wrestler in wrestlerSummaries"
          :key="wrestler.id"
          :class="cardClass()"
          :style="cardStyle()"
          :data-cy="`review-wrestler-card-${wrestler.id}`"
        >
          <div :class="cardHeaderClass()">
            <div class="flex align-items-center gap-sm flex-1 min-w-0">
              <img
                v-if="wrestler.avatar_url"
                :src="wrestler.avatar_url"
                :alt="wrestler.name"
                class="flex-shrink-0 w-12 h-12 object-cover rounded-xs border border-solid border-green-200"
              />
              <h3 class="m-y-none font-heavy min-w-0">
                {{ wrestler.name }}
              </h3>
            </div>
            <button
              class="btn btn-default btn-sm flex-shrink-0"
              :class="{ disabled: !editEnabled }"
              :disabled="!editEnabled"
              :data-cy="`review-edit-wrestler-${wrestler.id}`"
              @click="editStep('wrestler_info', { wrestlerId: wrestler.id })"
            >
              <i class="fa-solid fa-pencil m-r-xxs"></i>Edit Info
            </button>
          </div>
          <div :class="cardBodyClass()">
            <!-- Standard fields: 2-col icon grid -->
            <div class="row m-x-none">
              <div v-if="wrestler.dob" class="col-sm-6 p-y-xxs">
                <div class="flex align-items-center gap-sm">
                  <i class="fa-solid fa-cake-candles text-gray-400"></i>
                  <span class="text-gray-700">{{
                    formatDate(wrestler.dob)
                  }}</span>
                </div>
              </div>
              <div v-if="wrestler.grade" class="col-sm-6 p-y-xxs">
                <div class="flex align-items-center gap-sm">
                  <i class="fa-solid fa-graduation-cap text-gray-400"></i>
                  <span class="text-gray-700">{{ wrestler.grade }}</span>
                </div>
              </div>
              <div v-if="wrestler.weight_class" class="col-sm-6 p-y-xxs">
                <div class="flex align-items-center gap-sm">
                  <i class="fa-solid fa-gauge-high text-gray-400"></i>
                  <span class="text-gray-700">{{
                    formatWeight(wrestler.weight_class)
                  }}</span>
                </div>
              </div>
              <div v-if="wrestler.aau_id" class="col-sm-6 p-y-xxs">
                <div class="flex align-items-center gap-sm">
                  <i class="fa-regular fa-id-card text-gray-400"></i>
                  <span class="text-gray-700">
                    AAU {{ wrestler.aau_id }}
                    <span v-if="wrestler.aau_expires_on">
                      &middot; exp {{ formatDate(wrestler.aau_expires_on) }}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            <!-- USAW: full-width if expiring soon (so the badge has room) -->
            <div v-if="wrestler.usaw_id" class="m-t-xs">
              <div class="flex align-items-start gap-sm">
                <i
                  class="fa-regular fa-id-card text-gray-400 flex-shrink-0"
                  style="margin-top: 0.25rem"
                ></i>
                <div
                  class="flex flex-wrap align-items-center gap-xs"
                  style="min-width: 0"
                >
                  <span class="text-gray-700">
                    USAW {{ wrestler.usaw_id }}
                    <span v-if="wrestler.usaw_expires_on">
                      &middot; expires
                      {{ formatDate(wrestler.usaw_expires_on) }}
                    </span>
                  </span>
                  <span
                    v-if="usawExpiringSoon(wrestler.usaw_expires_on)"
                    class="badge-yellow"
                    :data-cy="`review-wrestler-usaw-expiring-${wrestler.id}`"
                    >Expires soon</span
                  >
                </div>
              </div>
            </div>

            <!-- Registration Q&A: divider + grouped 2-col stacked label/value -->
            <div
              v-if="wrestler.questions && wrestler.questions.length > 0"
              class="border-t border-solid border-green-300 m-t-sm p-t-sm"
            >
              <div
                v-for="(group, groupIdx) in groupQuestions(wrestler.questions)"
                :key="`wg-${wrestler.id}-${groupIdx}`"
                :class="groupIdx > 0 ? 'm-t-sm' : ''"
              >
                <h4 v-if="group.name" class="font-heavy m-b-xs m-t-none">
                  {{ group.name }}
                </h4>
                <div class="grid grid-cols-2 md-grid-cols-1 gap-4">
                  <div
                    v-for="q in group.questions"
                    :key="`wq-${wrestler.id}-${q.id}`"
                  >
                    <p class="text-12 text-gray-500 m-b-none">{{ q.prompt }}</p>
                    <template v-if="q.type === 'RegPhotoQuestion'">
                      <a
                        v-if="q.value"
                        :href="q.value"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          :src="q.value"
                          :alt="q.prompt"
                          class="w-24 h-24 object-cover rounded-xxs border border-solid border-gray-200"
                        />
                      </a>
                      <p v-else class="text-gray-700 m-b-none">—</p>
                    </template>
                    <p
                      v-else
                      class="text-gray-700 m-b-none"
                      :class="{
                        'whitespace-pre-line': q.type === 'RegAddressQuestion',
                      }"
                    >
                      {{ q.value || "—" }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div class="flex justify-end m-t-md">
          <ladda-button
            :loading="saving"
            el-class="btn-primary w-full-xs-only"
            @lbClicked="continueToPayment"
          >
            Confirm and continue to payment
          </ladda-button>
        </div>
        <div v-if="error" class="text-right m-t-xs">
          <small
            ><span class="text-danger">{{ errorMessage }}</span></small
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LaddaButton from '../../shared/ladda_button.vue';
import { funnelMixin } from './mixins/funnel_mix';

export default {
  name: 'funnel-review',
  mixins: [funnelMixin],
  components: {
    LaddaButton,
  },
  props: {
    funnel: {
      type: Boolean,
      default: true,
    },
    paidSessionName: {
      type: String,
      default: null,
    },
    parentSummary: {
      type: Object,
      required: true,
    },
    parentQuestions: {
      type: Array,
      default() {
        return [];
      },
    },
    wrestlerSummaries: {
      type: Array,
      default() {
        return [];
      },
    },
    agreementSummary: {
      type: Object,
      default() {
        return { all_signed: false };
      },
    },
    autoSkippedStates: {
      type: Array,
      default() {
        return [];
      },
    },
    forTeamRegistration: {
      type: Boolean,
      default: false,
    },
    editEnabled: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      saving: false,
      error: false,
      errorMessage: '',
    };
  },
  computed: {
    heading() {
      return this.paidSessionName
        ? `Review your registration for ${this.paidSessionName}`
        : 'Review your information';
    },
  },
  methods: {
    editStep(targetState, opts = {}) {
      if (!this.editEnabled) return;
      this.editFunnelStep(targetState, opts);
    },
    continueToPayment() {
      this.saving = true;
      this.error = false;
      this.goToNextFunnelStep({ reviewed_confirmed: true });
    },
    cardClass() {
      // border-l-4 utility zeroes the other 3 sides; use an inline override
      // for the thick left accent so the full 4-sided border still renders.
      return 'border border-solid border-green-300 rounded-xxs m-b-md';
    },
    cardStyle() {
      return { borderLeftWidth: '4px' };
    },
    cardHeaderClass() {
      return (
        'flex-col-xs-only align-items-start-xs-only gap-y-xs-xs-only flex align-items-center justify-space-between p-md p-b-sm '
        + 'border-b border-solid border-green-300 bg-green-50 rounded-t-xxs'
      );
    },
    cardBodyClass() {
      return 'p-md';
    },
    formatDate(value) {
      if (!value) return '';
      // moment parses date-only ISO strings ("1982-07-23") as LOCAL time. Native
      // `new Date(...)` parses them as UTC midnight, which renders a day earlier for
      // users west of UTC (WRE-674: DOB showing Jul 22 instead of Jul 23).
      return moment(value).format('MMM D, YYYY');
    },
    usawExpiringSoon(expires) {
      if (!expires) return false;
      const exp = new Date(expires);
      if (Number.isNaN(exp.getTime())) return false;
      const sixtyDays = 60 * 24 * 60 * 60 * 1000;
      const diff = exp.getTime() - Date.now();
      return diff > 0 && diff <= sixtyDays;
    },
    formatWeight(weight) {
      // weight_class is a free-form string. Append "lb" when the value is a plain number
      // so we don't end up with "Heavyweight lb" if a coach entered text instead.
      if (weight == null) return '';
      const str = String(weight).trim();
      return /^\d+(\.\d+)?$/.test(str) ? `${str} lb` : str;
    },
    groupQuestions(questions) {
      // Bucket questions by group_with so the REVIEW page mirrors the section structure
      // registration_info_form uses when collecting these answers. Questions with no
      // group_with sit in a header-less bucket; otherwise we render the group name above
      // the 2-col grid. Preserves insertion order (controller already returns .in_order).
      if (!Array.isArray(questions) || questions.length === 0) return [];
      const groups = [];
      const indexByName = new Map();
      questions.forEach((q) => {
        const raw = q.group_with;
        const name = raw && raw !== 'null' ? raw : '';
        if (!indexByName.has(name)) {
          indexByName.set(name, groups.length);
          groups.push({ name, questions: [] });
        }
        groups[indexByName.get(name)].questions.push(q);
      });
      return groups;
    },
  },
};
</script>
